/**
 * GZOO AI Marketing - Frontend
 * Client Providers Wrapper
 * Includes global 401 error handling for automatic logout
 */

'use client';

import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useState, useEffect, type ReactNode } from 'react';
import { Toaster } from 'sonner';
import { ThemeProvider } from './ThemeProvider';
import { BrandingProvider } from './BrandingProvider';

interface ProvidersProps {
  children: ReactNode;
}

// Handle 401 errors globally - clear token and redirect to login
function handleAuthError(error: unknown) {
  if (error instanceof Error && error.message.includes('401')) {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      window.location.href = '/login';
    }
  }
}

export function Providers({ children }: ProvidersProps) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        queryCache: new QueryCache({
          onError: (error) => handleAuthError(error),
        }),
        mutationCache: new MutationCache({
          onError: (error) => handleAuthError(error),
        }),
        defaultOptions: {
          queries: {
            staleTime: 5 * 60 * 1000, // 5 minutes
            retry: (failureCount, error) => {
              // Don't retry on 401 errors
              if (error instanceof Error && error.message.includes('401')) {
                return false;
              }
              return failureCount < 2;
            },
            refetchOnWindowFocus: false,
          },
        },
      })
  );

  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <BrandingProvider>{children}</BrandingProvider>
        {/* Global toast host — the app already calls sonner's toast() in several
            places (variants, brand kit, etc.) but nothing rendered them until now. */}
        <Toaster richColors position="top-right" />
        {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default Providers;
